// src/pages/OpsExceptionOverlay.jsx
import { useMemo, useState, useEffect, useRef } from "react";
import "./OpsExceptionOverlay.css";

import { ENV, tbPostJson } from "../api/client";
import { toast } from "react-toastify";

/**
 * Mono: ensure very long tokens (JWTs, encrypted values, etc.) wrap/break
 * so the user does not need horizontal scrolling.
 */
const Mono = ({ children }) => (
    <span
        className="opsd-mono"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
    >
        {children}
    </span>
);

/* =========================================================
   Retry orchestration (same rules as OpsExceptions)
   ========================================================= */

function deriveOrchestratorStage(stage) {
    const s = String(stage || "").trim().toUpperCase();
    if (s === "DTVID") return ["DTVID", "HINGE"];
    if (s === "HINGE") return ["HINGE"];
    if (s === "ATTUID") return ["ATTUID"];
    return null;
}

function deriveReqTypeFromOperation(operation) {
    const op = String(operation || "").toUpperCase();
    if (/(OFFBOARD|OFFBOARDING|TERMINATE|TERMINATION|DISCONNECT|DEPROVISION|REMOVE)/.test(op)) {
        return "offboarding";
    }
    return "onboarding";
}

function deriveEnv() {
    const v =
        ENV.APP_ENV ||
        ENV.ENV ||
        ENV.STAGE ||
        ENV.DEPLOY_ENV ||
        ENV.RUNTIME_ENV ||
        "";
    return String(v || "dev").trim() || "dev";
}

function buildOrchestratorPayloadFromRow(row) {
    const npwId = Number(row?.npw_id);
    if (!Number.isFinite(npwId) || npwId <= 0) return null;

    const stage = deriveOrchestratorStage(row?.stage);
    if (!stage) return null;

    const req_type = deriveReqTypeFromOperation(row?.operation);

    const client_request_id =
        row?.client_request_id ||
        row?.correlation_id ||
        undefined;

    return {
        env: deriveEnv(),
        req_type,
        npw_id: npwId,
        stage,
        ...(client_request_id ? { client_request_id: String(client_request_id) } : {}),
        meta: {
            source: "OpsExceptionOverlay",
            ex_id: row?.ex_id ?? null,
            error_code: row?.error_code ?? null,
            operation: row?.operation ?? null,
            stage_from_queue: row?.stage ?? null,
        }
    };
}

function extractErrMsg(e) {
    const msg =
        e?.message ||
        e?.response?.message ||
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.error ||
        e?.data?.error ||
        "";
    return String(msg || "Request failed");
}

function summarizeOrchestratorResponse(resp) {
    if (!resp || typeof resp !== "object") {
        return { ok: true, message: "Retry accepted." };
    }

    const topOk = resp?.summary?.ok ?? resp?.ok ?? null;
    const results = Array.isArray(resp?.results) ? resp.results : [];
    const totalStages = results.length;
    const okStages = results.filter((r) => r && r.ok === true).length;

    if (typeof topOk === "boolean") {
        if (topOk) {
            const msg = totalStages
                ? `Retry succeeded (${okStages}/${totalStages} stages).`
                : "Retry succeeded.";
            return { ok: true, message: msg };
        }
        const msg = totalStages
            ? `Retry failed (${okStages}/${totalStages} stages).`
            : "Retry failed.";
        return { ok: false, message: msg };
    }

    if (totalStages) {
        return okStages === totalStages
            ? { ok: true, message: `Retry succeeded (${okStages}/${totalStages} stages).` }
            : { ok: false, message: `Retry failed (${okStages}/${totalStages} stages).` };
    }

    return { ok: true, message: "Retry completed." };
}

export default function OpsExceptionOverlay({ row, onClose }) {
    const [tab, setTab] = useState("request");

    // Wrap ON by default to avoid horizontal scrolling for long attributes/values
    const [wrap, setWrap] = useState(true);

    const [retrying, setRetrying] = useState(false);
    const panelRef = useRef(null);

    const reqJson = useMemo(() => JSON.stringify(row.request_payload ?? {}, null, 2), [row]);
    const resJson = useMemo(() => JSON.stringify(row.response_payload ?? {}, null, 2), [row]);
    const rawJson = useMemo(() => JSON.stringify(row ?? {}, null, 2), [row]);

    const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : "—");
    const statusClass = `opsd-badge st-${String(row.status || "").toLowerCase()}`;

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        panelRef.current?.focus();
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const copy = (t) => navigator.clipboard?.writeText(t).catch(() => { });
    const download = (name, text) => {
        const blob = new Blob([text], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name; a.click();
        URL.revokeObjectURL(url);
    };

    /**
     * Single-toast pattern:
     * - show "submitted" as loading toast (unique toastId)
     * - update same toast to success/error when response arrives
     * This guarantees no duplicate submitted toasts.
     */
    const handleRetry = async () => {
        if (retrying) return;

        const retryUrl = ENV.EXCEPTION_RETRY_URL;
        const payload = buildOrchestratorPayloadFromRow(row);

        if (!retryUrl) {
            toast.error("Retry URL is not configured (ENV.EXCEPTION_RETRY_URL).");
            return;
        }
        if (!payload) {
            toast.error("Cannot retry: missing NPW ID or invalid stage.");
            return;
        }

        const toastId = `opsx-retry-${row?.ex_id ?? payload.npw_id}`;

        setRetrying(true);

        // Show ONE submitted toast (as loading)
        if (!toast.isActive(toastId)) {
            toast.loading(`Retry submitted for NPW ${payload.npw_id}.`, { toastId });
        } else {
            // If somehow active, ensure it remains in loading state
            toast.update(toastId, {
                render: `Retry submitted for NPW ${payload.npw_id}.`,
                isLoading: true,
                type: "default",
                autoClose: false
            });
        }

        try {
            const resp = await tbPostJson(retryUrl, payload);
            const summary = summarizeOrchestratorResponse(resp);

            toast.update(toastId, {
                render: `NPW ${payload.npw_id}: ${summary.message}`,
                type: summary.ok ? "success" : "error",
                isLoading: false,
                autoClose: 4000,
                closeOnClick: true
            });
        } catch (e) {
            toast.update(toastId, {
                render: `NPW ${payload.npw_id}: Retry failed • ${extractErrMsg(e)}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true
            });
        } finally {
            setRetrying(false);
        }
    };

    // Strong wrap/break styling applied inline (so it works even if CSS is missing/old)
    const preStyleWrap = {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    };

    const preStyleNoWrap = {
        whiteSpace: "pre",        // keep formatting
        overflowX: "auto",        // allow scroll only if user explicitly turns wrap off
    };

    return (
        <div className="opsd-sheet-backdrop" onClick={onClose}>
            <section
                className="opsd-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="Exception details"
                tabIndex={-1}
                ref={panelRef}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <header className="opsd-hero">
                    <div className="opsd-hero-left">
                        <h2 className="opsd-h2">Exception</h2>
                        <span className="opsd-sub"><Mono>#{row.ex_id}</Mono></span>
                        <span className={statusClass}>{row.status}</span>
                        {row.stage ? <span className="opsd-pill">{row.stage}</span> : null}
                        {row.npw_id ? <span className="opsd-sub">NPW {row.npw_id}</span> : null}
                    </div>

                    <div className="opsd-hero-right">
                        <button className="opsd-btn" onClick={onClose} disabled={retrying}>Close</button>
                        <button className="opsd-btn primary" onClick={handleRetry} disabled={retrying}>
                            {retrying ? "Retrying..." : "Retry"}
                        </button>
                    </div>
                </header>

                {retrying && (
                    <div className="opsd-busy" style={{ padding: "10px 16px", fontSize: 13 }}>
                        Retrying… waiting for orchestrator response.
                    </div>
                )}

                {/* BODY */}
                <div className="opsd-content">
                    <div className="opsd-main">
                        {/* LEFT */}
                        <div className="opsd-card">
                            <div className="opsd-stats">
                                <div className="opsd-stat"><div className="k">HTTP</div><div className="v">{row.http_status}</div></div>
                                <div className="opsd-stat"><div className="k">Error Code</div><div className="v">{row.error_code}</div></div>
                                <div className="opsd-stat"><div className="k">Retries</div><div className="v">{row.retry_count + "/" + row.max_retries}</div></div>
                                <div className="opsd-stat"><div className="k">Owner</div><div className="v">{row.owner}</div></div>
                            </div>

                            <div className="opsd-head">Summary</div>
                            <div className="opsd-grid">
                                <div className="k">Message</div>
                                <div className="v"><Mono>{row.error_message}</Mono></div>
                            </div>

                            <div className="opsd-tabsbar mt8">
                                <div className="opsd-tabs">
                                    {["request", "response", "raw"].map((t) => (
                                        <button
                                            key={t}
                                            className={`opsd-tab ${tab === t ? "active" : ""}`}
                                            onClick={() => setTab(t)}
                                            disabled={retrying}
                                        >
                                            {t[0].toUpperCase() + t.slice(1)}
                                        </button>
                                    ))}
                                </div>

                                <div className="opsd-tools">
                                    <button className="opsd-btn sm" onClick={() => setWrap((w) => !w)} disabled={retrying}>
                                        {wrap ? "No Wrap" : "Wrap"}
                                    </button>
                                    <button
                                        className="opsd-btn sm"
                                        onClick={() => copy(tab === "request" ? reqJson : tab === "response" ? resJson : rawJson)}
                                        disabled={retrying}
                                    >
                                        Copy
                                    </button>
                                    <button
                                        className="opsd-btn sm"
                                        disabled={retrying}
                                        onClick={() => {
                                            const [name, data] =
                                                tab === "request" ? ["request.json", reqJson] :
                                                    tab === "response" ? ["response.json", resJson] :
                                                        ["exception.json", rawJson];
                                            download(name, data);
                                        }}
                                    >
                                        Download
                                    </button>
                                </div>
                            </div>

                            {tab === "request" && (
                                <pre className={`opsd-code ${wrap ? "wrap" : ""}`} style={wrap ? preStyleWrap : preStyleNoWrap}>
                                    {reqJson}
                                </pre>
                            )}
                            {tab === "response" && (
                                <pre className={`opsd-code ${wrap ? "wrap" : ""}`} style={wrap ? preStyleWrap : preStyleNoWrap}>
                                    {resJson}
                                </pre>
                            )}
                            {tab === "raw" && (
                                <pre className={`opsd-code ${wrap ? "wrap" : ""}`} style={wrap ? preStyleWrap : preStyleNoWrap}>
                                    {rawJson}
                                </pre>
                            )}
                        </div>

                        {/* RIGHT */}
                        <aside className="opsd-card side">
                            <div className="opsd-head">Timeline</div>
                            <div className="opsd-timeline">
                                <div className="step"><div className="icon updated" /><div className="txt"><div className="t">Updated</div><div>{fmt(row.updated_at)}</div></div></div>
                                <div className="step"><div className="icon next" /><div className="txt"><div className="t">Next Retry</div><div>{fmt(row.next_retry_at)}</div></div></div>
                                <div className="step"><div className="icon error" /><div className="txt"><div className="t">Last Error</div><div>{fmt(row.last_error_at)}</div></div></div>
                            </div>

                            <div className="opsd-head mt12">Meta</div>
                            <div className="meta"><span className="d">Stage</span><span>{row.stage}</span></div>
                            <div className="meta"><span className="d">Owner</span><span>{row.owner}</span></div>
                            <div className="meta"><span className="d">NPW</span><span>{row.npw_id}</span></div>
                            <div className="meta"><span className="d">Operation</span><span><Mono>{row.operation}</Mono></span></div>
                            <div className="meta"><span className="d">Correlation Id</span><span><Mono>{row.correlation_id}</Mono></span></div>
                        </aside>
                    </div>
                </div>
            </section>
        </div>
    );
}
